import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './SlidingFixed.scss';
import classNames from 'classnames';
import { useMutationObserver, useWindowEventListener } from 'rooks';

interface Props {
  children?: ReactNode;
  className?: string;
}

const SlidingFixedParent = ({ children, className }: Props) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [lockedClass, setLockedClass] = useState<string>(null);

  const update = useCallback(() => {
    if (parentRef.current == null) {
      if (lockedClass !== null) {
        setLockedClass(null);
      }
      return;
    }

    const parentStyle = window.getComputedStyle(parentRef.current);

    if (parentStyle.getPropertyValue('--sliding-fixed-mode').trim().toLowerCase() !== 'enabled') {
      if (lockedClass !== null) {
        setLockedClass(null);
      }
      return;
    }

    const sf = {
      offsetTop: parseFloat(parentStyle.getPropertyValue('--sliding-fixed-offset-top')) || 0,
      offsetBottom: parseFloat(parentStyle.getPropertyValue('--sliding-fixed-offset-bottom')) || 0,
      height: parseFloat(parentStyle.getPropertyValue('--sliding-fixed-height')) || 0,
    };

    const rect = parentRef.current.getBoundingClientRect();

    const y1 = 2 * (rect.top + sf.offsetTop) + sf.height - window.innerHeight;
    const y2 = 2 * (rect.bottom - sf.offsetBottom) - sf.height - window.innerHeight;

    setLockedClass(
      y1 > y2 || rect.height < window.innerHeight
        ? 'sliding-fixed-parent-locked-y-center'
        : 0 < y1
        ? 'sliding-fixed-parent-locked-y-top'
        : 0 >= y2
        ? 'sliding-fixed-parent-locked-y-bottom'
        : 'sliding-fixed-parent-locked-y-none'
    );
  }, [lockedClass]);

  useLayoutEffect(update, [update]);
  useWindowEventListener('resize', update);
  useWindowEventListener('scroll', update);
  useMutationObserver(parentRef, update);

  return (
    <div className={classNames({ ['sliding-fixed-parent']: !!lockedClass }, lockedClass, className)} ref={parentRef}>
      {children}
    </div>
  );
};

export default SlidingFixedParent;
